import React, { useEffect, useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Formik } from "formik";
import { useTranslation } from "react-i18next";
import Common from "us.common";
import { AccountsValidationSchema } from "us.collection.economy/components/ClientAccounts/Validations";
import * as Actions from "us.collection.economy/actions";
import { SaveClientAccounts } from "us.collection.economy/components/ClientAccounts/Repository";
import {
  isFormDirty,
  getButtonTitle,
  getClientAccountById,
} from "us.collection.economy/components/ClientAccounts/Functions";
import { useLocation } from "react-router-dom";
import { ClientAccountsAction } from "us.collection.economy/components/ClientAccounts/Constants";
import { RootState } from "us.helper/types";

const {
  $Switch,
  $Button,
  $Form,
  $Row,
  $Col,
  $Select,
  $Input,
  $AsyncInput,
  $AsyncInputAccountNo,
  $Popconfirm,
} = Common.Components;

type ClientAccountDetailsProps = {
  onCancel: () => void;
};

/**
 * @description - Drawer View for Add, Edit Client Accounts
 * @link https://unicorn-solutions.atlassian.net/wiki/spaces/USU/pages/2835709953/Client+Accounts
 * @author Kaushalya Sandamali <kaushalyas@unicorn-solutions.com>
 * @since 20/06/2022
 */
const ClientAccountDetail: React.FC<
  PropsFromRedux & ClientAccountDetailsProps
> = (props) => {
  const { t } = useTranslation();

  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const action = urlParams.get("action");
  const isEdit = action === ClientAccountsAction.EDIT;
  const accountId = urlParams.get("accountId");

  const {
    ledgerAccounts,
    clientAccountsList,
    remitSequenceDetails,
    ledgerSequenceDetails,
    isValidAccountNo,
    isValidJournalPrefix,
    accountSaveUpdate,
    initDetails,
    addNewClientAccount,
    onCancel,
    updateClientAccounts,
    validateAccountNo,
    resetAccountNoValidation,
    validateJournalPrefix,
    resetJournalPrefixValidation,
    validateIBANNumber,
    isValidIBANNo
  } = props;

  const initialValues = useMemo(() => {
    return getClientAccountById(clientAccountsList, Number(accountId));
  }, [clientAccountsList, accountId]);

  useEffect(() => {
    initDetails && initDetails({});
  }, []);

  useEffect(() => {
    if (
      !accountSaveUpdate.isLoading &&
      accountSaveUpdate.data.hasOwnProperty("accountId")
    ) {
      onCancel();
    }
  }, [accountSaveUpdate.isLoading]);

  /**
   * @description - Handle form submit
   * @param {any} values - Form values
   * @param {any} actions - Form Actions
   */
  const handleSubmit = (values: any, actions: any) => {
    actions.setSubmitting(true);
    const payload = SaveClientAccounts.call({ ...values, accountId });
    if (isEdit) {
      updateClientAccounts && updateClientAccounts(payload);
    } else {
      addNewClientAccount && addNewClientAccount(payload);
    }
  };

  /**
   * @description - clear account no input value
   */
  const onClearAccountNoInput = () => {
    resetAccountNoValidation && resetAccountNoValidation({});
  };

  /**
   * @description - clear journal prefix input
   */
  const onClearJournalPrefixInput = () => {
    resetJournalPrefixValidation && resetJournalPrefixValidation({});
  };

  return (
    <Formik
      initialValues={{
        ...initialValues,
      }}
      enableReinitialize
      validationSchema={AccountsValidationSchema(isValidIBANNo)}
      validateOnMount
      validateOnBlur
      validateOnChange
      onSubmit={handleSubmit}
    >
      {({
        values,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
        isValidating,
        resetForm,
        errors,
        ...restProps
      }: any) => (
        <>
          <$Form layout="vertical" autoComplete="off" onSubmit={handleSubmit}>
            <$Row gutter={16} className="mb-2">
              <$Col span={12}>
                <$AsyncInputAccountNo
                  label={t(
                    "US.COLLECTION.ECONOMY:CLIENT_ACCOUNTS.ACCOUNT_NUMBER"
                  )}
                  className="w-100"
                  name="accountNo"
                  placeholder={t(
                    "US.COLLECTION.ECONOMY:CLIENT_ACCOUNTS.ACCOUNT_NUMBER"
                  )}
                  size="small"
                  required={true}
                  allowClear
                  onBlur={(e: any) => {
                    const { value } = e.target;
                    if (
                      value !== "" &&
                      value != 0 &&
                      value.replace(/ /g, "") !== initialValues.accountNo
                    ) {
                      const accountNum = value.replace(/ /g, "");
                      validateAccountNo && validateAccountNo(accountNum);
                    }
                  }}
                  isValid={isValidAccountNo}
                  asyncError={t(
                    "US.COLLECTION.VALIDATIONS:INVALID.DUPLICATE_ACCOUNT_NUMBER"
                  )}
                  onClear={onClearAccountNoInput}
                  disabled={isEdit && values.isUsed}
                />
              </$Col>
              <$Col span={12}>
                <$Input
                  required
                  size="small"
                  name="accountName"
                  label={t(
                    "US.COLLECTION.ECONOMY:CLIENT_ACCOUNTS.ACCOUNT_NAME"
                  )}
                />
              </$Col>
            </$Row>
            <$Row gutter={16} className="mb-2">
              <$Col span={12}>
              <$Input
                  size="small"
                  name="bICNumber"
                  label={t(
                    "US.COLLECTION.ECONOMY:CLIENT_ACCOUNTS.BIC"
                  )}
                  maxLength={11}
                  required
                />
              </$Col>
              <$Col span={12}>
                <$Input
                  size="small"
                  name="iBANNumber"
                  label={t(
                    "US.COLLECTION.ECONOMY:CLIENT_ACCOUNTS.IBAN"
                  )}
                  maxLength={34}
                  required
                  onBlur={(e: any) => {
                    if (e.target.value !== "" && e.target.value != 0) {
                      const iBANNum = e.target.value.replace(/ /g, "");
                      validateIBANNumber && validateIBANNumber(iBANNum);
                    }
                  }}
                />
              </$Col>
            </$Row>
            <$Row gutter={16} className="mb-2">
              <$Col span={12}>
                <$Select
                  name="ledgerAccountNumber"
                  data-testid="ledger-account"
                  formitem={{
                    label: t(
                      "US.COLLECTION.ECONOMY:CLIENT_ACCOUNTS.LEDGER_ACCOUNT"
                    ),
                  }}
                  size="small"
                  options={ledgerAccounts}
                  allOption={false}
                  optionValue="accountNo"
                  optionText={"accountName"}
                  onSearchBy={["accountName", "accountNo"]}
                  tabIndex={1}
                  required
                />
              </$Col>
              <$Col span={12}>
                <$Select
                  name="ledgerVoucherSequence"
                  data-testid="ledger-Voucher-Sequence"
                  formitem={{
                    label: t(
                      "US.COLLECTION.ECONOMY:CLIENT_ACCOUNTS.LEDGER_VOUCHER_SEQUENCE"
                    ),
                  }}
                  size="small"
                  options={ledgerSequenceDetails}
                  allOption={false}
                  optionValue="subSequenceId"
                  optionText={"text"}
                  onSearchBy={["sequenceId", "subSequenceId"]}
                  tabIndex={1}
                  required
                />
              </$Col>
            </$Row>
            <$Row gutter={16} className="mb-2">
              <$Col span={12}>
                <$AsyncInput
                  name="remitJournalPrifix"
                  label={t(
                    "US.COLLECTION.ECONOMY:CLIENT_ACCOUNTS.REMIT_JOURNAL_PREFIX"
                  )}
                  dataTestid="async-input-remitJournalPrifix"
                  placeholder={"XXXX"}
                  size="small"
                  autoFocus={false}
                  maxLength={4}
                  onBlur={(e: any) => {
                    const { value } = e.target;
                    if (
                      value !== "" &&
                      value != 0 &&
                      value !== initialValues.remitJournalPrifix
                    ) {
                      validateJournalPrefix && validateJournalPrefix(value);
                    }
                  }}
                  isValid={isValidJournalPrefix}
                  onClear={onClearJournalPrefixInput}
                  asyncError={t(
                    "US.COLLECTION.VALIDATIONS:INVALID.DUPLICATE_REMIT_JOURNAL_PREFIX"
                  )}
                />
              </$Col>
              <$Col span={12}>
                <$Select
                  name="remitVoucherSequence"
                  data-testid="remit-Voucher-Sequence"
                  formitem={{
                    label: t(
                      "US.COLLECTION.ECONOMY:CLIENT_ACCOUNTS.REMIT_VOUCHER_SEQUENCE"
                    ),
                  }}
                  size="small"
                  options={remitSequenceDetails}
                  allOption={false}
                  optionValue="subSequenceId"
                  optionText={"text"}
                  onSearchBy={["sequenceId", "subSequenceId"]}
                  tabIndex={1}
                />
              </$Col>
            </$Row>
            <$Row gutter={16} className="mb-2">
              <$Col span={12}>
                <$Select
                  name="remitTransactionSequence"
                  data-testid="remit-Transaction-Sequence"
                  formitem={{
                    label: t(
                      "US.COLLECTION.ECONOMY:CLIENT_ACCOUNTS.REMIT_TRANSACTION_SEQUENCE"
                    ),
                  }}
                  size="small"
                  options={remitSequenceDetails}
                  allOption={false}
                  optionValue="subSequenceId"
                  optionText={"text"}
                  onSearchBy={["sequenceId", "subSequenceId"]}
                  tabIndex={1}
                />
              </$Col>
              <$Col span={12}>
                <$Select
                  name="remitDaySequence"
                  data-testid="remit-Day-Sequence"
                  formitem={{
                    label: t(
                      "US.COLLECTION.ECONOMY:CLIENT_ACCOUNTS.REMIT_DAY_SEQUENCE"
                    ),
                  }}
                  size="small"
                  options={remitSequenceDetails}
                  allOption={false}
                  optionValue="subSequenceId"
                  optionText={"text"}
                  onSearchBy={["sequenceId", "subSequenceId"]}
                  tabIndex={1}
                />
              </$Col>
            </$Row>
            <$Row gutter={16} className="mb-2">
              <$Col span={12}>
                <$Select
                  name="remitJournalSequence"
                  data-testid="remit-Journal-Sequence"
                  formitem={{
                    label: t(
                      "US.COLLECTION.ECONOMY:CLIENT_ACCOUNTS.REMIT_JOURNAL_SEQUENCE"
                    ),
                  }}
                  size="small"
                  options={remitSequenceDetails}
                  allOption={false}
                  optionValue="subSequenceId"
                  optionText={"text"}
                  onSearchBy={["sequenceId", "subSequenceId"]}
                  tabIndex={1}
                />
              </$Col>
              <$Col span={12}>
                <$Input
                  size="small"
                  name="enterpriseNumber"
                  label={t(
                    "US.COLLECTION.ECONOMY:CLIENT_ACCOUNTS.ENTERPRISE_NUMBER"
                  )}
                  maxLength={11}
                />
              </$Col>
            </$Row>
            <$Row gutter={16}>
              <$Col span={12}>
                <div className="d-flex">
                  <label className="mr-2">
                    {t(
                      "US.COLLECTION.ECONOMY:CLIENT_ACCOUNTS.CONSIDER_FOR_ORDERLINES"
                    )}
                  </label>
                  <div>
                    <$Switch
                      size="small"
                      name="isConsideredForOrderlines"
                      checked={values?.isConsideredForOrderlines}
                    />
                  </div>
                </div>
              </$Col>
            </$Row>
          </$Form>
          <div className="drawer-footer-fixed align-content-center justify-content-end">
            <div>
              <$Button
                className="ml-3 mr-2"
                type="primary"
                data-testid="save-btn"
                onClick={handleSubmit}
                disabled={
                  !restProps.isValid ||
                  !isValidAccountNo ||
                  !isValidJournalPrefix ||
                  !isFormDirty(values, initialValues)
                }
                loading={accountSaveUpdate?.isLoading}
              >
                {t(getButtonTitle(isEdit))}
              </$Button>
              {isFormDirty(values, initialValues) && (
                <$Popconfirm
                  title={t(
                    "US.COLLECTION.ECONOMY:COVERING_SEQUENCE.ARE_YOU_SURE_YOU_WANT_TO_DISCARD_THE_CHANGES_?"
                  )}
                  placement="topLeft"
                  onConfirm={onCancel}
                  okText={t("COMMON.YES")}
                  cancelText={t("COMMON.NO")}
                >
                  <$Button>{t("US.COLLECTION.COMMON:COMMON.CANCEL")}</$Button>
                </$Popconfirm>
              )}
              {!isFormDirty(values, initialValues) && (
                <$Button onClick={onCancel}>
                  {t("US.COLLECTION.COMMON:COMMON.CANCEL")}
                </$Button>
              )}
            </div>
          </div>
        </>
      )}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => {
  const { clientAccounts } = state;

  const {
    ledgerAccounts,
    remitSequenceDetails,
    ledgerSequenceDetails,
    clientAccountsList,
    isValidAccountNo,
    isValidJournalPrefix,
    accountSaveUpdate,
    isValidIBANNo
  } = clientAccounts;

  return {
    ledgerAccounts,
    remitSequenceDetails,
    ledgerSequenceDetails,
    clientAccountsList,
    isValidAccountNo,
    isValidJournalPrefix,
    accountSaveUpdate,
    isValidIBANNo
  };
};

const {
  initDetails,
  newClientAccount,
  existingClientAccount,
  accountNumber,
  journalPrefix,
  iBANNumber,
} = Actions.clientAccounts;

const { get } = initDetails;
const { save } = newClientAccount;
const { update } = existingClientAccount;

const mapDispatchToProps = {
  initDetails: get,
  addNewClientAccount: save,
  updateClientAccounts: update,
  validateAccountNo: accountNumber.getById,
  resetAccountNoValidation: accountNumber.reset,
  validateJournalPrefix: journalPrefix.getById,
  resetJournalPrefixValidation: journalPrefix.reset,
  validateIBANNumber: iBANNumber.getById
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(ClientAccountDetail);
